import { Container, PointData, Sprite } from "pixi.js";
import { Sheets } from "./Textures";
import { randomInt, randomRange, weightedSample } from "../random";
import { Game } from "./Game";
import { Geom } from "./Geom";
import { Enemies } from "./Enemies";
import { Hive } from "./Hive";
import { KeyIcon } from "./KeyIcon";
import { Item } from "./Item";
import { BeeType } from "./BeeType";

const chunkRadius = 10.5;
const tileSize = 32;
const loadedChunkDepth = 2;

function getChunk(location: PointData): PointData {
	const f = (n: number) => n / tileSize / (chunkRadius * 2) | 0;
	return {
		x: f(location.x),
		y: f(location.y)
	}
}

interface ChunkData {
	id: string;
	pointId: PointData;
}

export class Ground {
	public readonly container = new Container({ isRenderGroup: true, interactiveChildren: false });
	public currentChunk: PointData = { x: 0, y: 0 }
	public readonly loadedChunks: PointData[] = [];
	private keyIcons: KeyIcon[] = [];
	public minX: number = Number.POSITIVE_INFINITY;
	public maxX: number = Number.NEGATIVE_INFINITY;
	public minY: number = Number.POSITIVE_INFINITY;
	public maxY: number = Number.NEGATIVE_INFINITY;

	public constructor(parent: Container, game: Game, chunks: number) {
		if (chunks % 2 == 0) {
			chunks++;
		}

		const itemsXOffset = 32 * 11;
		const attackXOffset = 32 * 20;
		if (game.stage == 0) {
			for (let x = 0; x < 2; x++) {
				for (let y = 0; y < 2; y++) {
					const jitter = 8;
					const bee = new Item(game.camera.container, BeeType.DiesFirstBee);
					bee.x = itemsXOffset + x * 18 - 32 + Math.random() * jitter - jitter / 2;
					bee.y = y * 18 + Math.random() * jitter - jitter / 2;
					bee.sprite.roundPixels = true;
					game.items.push(bee);
				}
			}

			for (let x = 0; x < 1; x++) {
				for (let y = 0; y < 2; y++) {
					const jitter = 8;
					const bee = new Item(game.camera.container, BeeType.MeleeBee);
					bee.x = attackXOffset + x * 18 - 32 + Math.random() * jitter - jitter / 2;
					bee.y = y * 18 + Math.random() * jitter - jitter / 2;
					bee.sprite.roundPixels = true;
					game.items.push(bee);
				}
			}
		}

		let rad = 3;
		if (game.stage == 2) rad = 5;
		if (game.stage == 3) rad = 5;
		let width: [number, number] = [rad * tileSize, rad * tileSize];
		let height: [number, number] = [rad * tileSize, rad * tileSize];
		if (game.stage == 0) {
			width = [8, 63];
			height = [8, 8];
		}

		game.hives = game.stage == 0
			? [new Hive(game.camera.container, { x: 32 * 55, y: 0 })]
			: Hive.spawnTriangle(game.camera.container, (width[0] - 20) * tileSize);

		if (game.stage == 0) {
			game.enemies.push(Enemies.createTutorialSpider(game.camera.container, game.stage, 32 * 28, -5 * 32));
			game.enemies.push(Enemies.createTutorialSpider(game.camera.container, game.stage, 32 * 28, 5 * 32));
			game.enemies.push(Enemies.createTutorialBird(game.camera.container, game.stage, 32 * 60, -3 * 32));
		}

		for (let x = -width[0]; x < width[1]; x++) {
			for (let y = -height[0]; y < height[1]; y++) {
				let tileIndex = weightedSample([
					[300, 0],
					[10, 1],
					[10, 2],
					[game.stage == 0 ? 0 : 1, 3],
					[1, 4],
				]);

				// if (
				// 	Math.abs((x - chunkRadius) % (chunkRadius * 2)) <= 0.5
				// 	|| Math.abs((y - chunkRadius) % (chunkRadius * 2)) <= 0.5
				// ) {
				// 	tileIndex = 3;
				// }

				let tile = new Sprite(Sheets.ground[tileIndex]);
				tile.x = x * tileSize;
				tile.y = y * tileSize;
				if (tile.x < this.minX) this.minX = tile.x;
				if (tile.x + tile.width > this.maxX) this.maxX = tile.x + tile.width;
				if (tile.y < this.minY) this.minY = tile.y;
				if (tile.y + tile.height > this.maxY) this.maxY = tile.y + tile.height;
				if (game.stage == 0 || game.stage == 1) {
				} else if (game.stage == 2) {
					tile.tint = 0xE47330;
				} else if (game.stage == 3) {
					tile.tint = 0xFF1210;
				}
				this.container.addChild(tile);


				if (game.stage == 0) {
					for (const hive of game.hives) {
						const distance = Geom.distance({ x: x * tileSize, y: y * tileSize }, hive);
						if (distance < 200) {
							tile.texture = Sheets.ground[3];
						} else if (distance < 800) {
							if (Math.random() < Geom.remap(100, 800, distance, 0.5, 0))
								tile.texture = Sheets.ground[3];
						}
					}
				} else {
					for (const hive of game.hives) {
						for (let angle = 0; angle < 2 * Math.PI; angle += 2 * Math.PI / 10) {
							const jitter = 64;
							const distance = Geom.distance({ x: x * tileSize, y: y * tileSize }, hive);
							if (distance > 2000) continue;
							if (Geom.distanceFromPointToLine(
								x * tileSize + jitter * Math.random(), y * tileSize + jitter * Math.random(),
								hive.x, hive.y,
								hive.x + Math.cos(angle), hive.y + Math.sin(angle),
							) < Geom.remap(0, 2000, distance, 8, 256)) {
								if (Math.random() * 100 < Geom.remap(0, 2000, distance, 50, 0.5)) tile.texture = Sheets.ground[3];
								break;
							}
						}
					}
				}
			}
		}
		parent.addChildAt(this.container, 0);

		if (game.stage == 0) {
			const yOffset = 4 * 32;

			{
				const spacing = 28;
				this.keyIcons.push(new KeyIcon(this.container, { x: 0, y: -yOffset - spacing }, 'W'))
				this.keyIcons.push(new KeyIcon(this.container, { x: -spacing, y: -yOffset }, 'A'))
				this.keyIcons.push(new KeyIcon(this.container, { x: 0, y: -yOffset }, 'S'))
				this.keyIcons.push(new KeyIcon(this.container, { x: spacing, y: -yOffset }, 'D'))
				let movement = new KeyIcon(this.container, { x: 58, y: -yOffset + 30 }, '');
				movement.graphics.visible = false;
				movement.keyText.visible = false;
				movement.description.text = 'Movement';
				movement.description.visible = true;
				this.keyIcons.push(movement)
			}

			{
				this.keyIcons.push(new KeyIcon(this.container, { x: itemsXOffset, y: -yOffset }, 'E'))
				let description = new KeyIcon(this.container, { x: itemsXOffset + 55, y: -yOffset + 30 }, '');
				description.graphics.visible = false;
				description.keyText.visible = false;
				description.description.text = 'Assimilate';
				description.description.visible = true;
				this.keyIcons.push(description)
			}

			{
				this.keyIcons.push(new KeyIcon(this.container, { x: attackXOffset, y: -yOffset }, 'SPACE'))
				let description = new KeyIcon(this.container, { x: attackXOffset + 42, y: -yOffset + 30 }, '');
				description.graphics.visible = false;
				description.keyText.visible = false;
				description.description.text = 'Attack';
				description.description.visible = true;
				this.keyIcons.push(description)
			}

			for (const keyIcon of this.keyIcons) {
				keyIcon.container.visible = true;
				this.container.addChild(keyIcon.container);
			}
		}

		this.generateLoot(game, width, height);

		for (const chunk of this.getRequiredChunks(this.currentChunk)) {
			this.loadChunk(game, chunk, (chunk.x == this.currentChunk.x && chunk.y == this.currentChunk.y) ? false : true)
		}
	}

	private generateLoot(game: Game, width: [number, number], height: [number, number]) {
		if (game.stage == 0) return;
		width = [Math.ceil(width[0] / (2 * chunkRadius)), Math.floor(width[1] / (2 * chunkRadius))];
		height = [Math.ceil(height[0] / (2 * chunkRadius)), Math.floor(height[1] / (2 * chunkRadius))];
		for (let chunkX = -width[0] + 1; chunkX < width[1]; chunkX++) {
			for (let chunkY = -height[0] + 1; chunkY < height[1]; chunkY++) {
				if (chunkX == 0 && chunkY == 0) continue;
				if (game.stage == 1) {
					if (randomRange(0, 100) > 25) continue;
				} else if (game.stage == 2) {
					if (randomRange(0, 100) > 25) continue;
				} else if (game.stage == 3) {
					if (randomRange(0, 100) > 30) continue;
				}
				const chunkId = { x: chunkX, y: chunkY };
				if (this.containsItem(game, chunkId)) continue;
				let minX = (chunkX * 2 * chunkRadius - chunkRadius) * tileSize;
				let maxX = (chunkX * 2 * chunkRadius + chunkRadius) * tileSize;
				let minY = (chunkY * 2 * chunkRadius - chunkRadius) * tileSize;
				let maxY = (chunkY * 2 * chunkRadius + chunkRadius) * tileSize;
				const padding = 3;
				minX += padding * tileSize;
				maxX -= padding * tileSize;
				minY += padding * tileSize;
				maxY -= padding * tileSize;

				const spawnPoint = { x: randomRange(minX, maxX), y: randomRange(minY, maxY) };
				const spawnAmount = randomRange(2, 3);
				let spawnType: BeeType = BeeType.DiesFirstBee;
				if (game.stage == 1) {
					spawnType = weightedSample([
						[1, BeeType.DiesFirstBee],
						[1, BeeType.DashBee],
						[1, BeeType.MeleeBee],
					]);
				} else if (game.stage == 2) {
					spawnType = weightedSample([
						[1, BeeType.DiesFirstBee],
						[1, BeeType.DashBee],
						[1, BeeType.BulletBee],
						[1, BeeType.MeleeBee],
					]);
				} else if (game.stage == 3) {
					spawnType = weightedSample([
						[1, BeeType.DashBee],
						[1, BeeType.BulletBee],
						[1, BeeType.MeleeBee],
					]);
				}
				for (let i = 0; i < spawnAmount; i++) {
					const item = new Item(game.camera.container, spawnType);
					item.x = spawnPoint.x + randomRange(0, 32);
					item.y = spawnPoint.y + randomRange(0, 32);
					game.items.push(item);
				}
			}
		}

	}

	public unloadChunk(game: Game, chunkId: PointData) {
		for (const enemy of game.enemies) {
			if (Geom.equal(getChunk(enemy), chunkId)) {
				enemy.destroy();
			}
		}

		this.loadedChunks.splice(this.loadedChunks.findIndex(c => Geom.equal(chunkId, c)), 1)
	}

	public loadChunk(game: Game, chunkId: PointData, spawnEnemies: boolean) {
		if (!game.spawnEnemies) spawnEnemies = false;
		this.loadedChunks.push(chunkId)
		let minEnemies = 1;
		let maxEnemies = 1;
		if (game.stage == 1) minEnemies = 0;
		if (game.stage == 1) maxEnemies = 0;
		if (game.stage == 2) minEnemies = 0;
		if (game.stage == 2) maxEnemies = 2;
		if (game.stage == 3) minEnemies = 0;
		if (game.stage == 3) maxEnemies = 3;
		let enemiesToCreate = randomInt(minEnemies, maxEnemies);
		const item = this.containsItem(game, chunkId);
		if (item instanceof Item) {
			if (game.stage == 1) enemiesToCreate = 2;
			if (game.stage == 2) enemiesToCreate = 3;
			if (game.stage == 3) enemiesToCreate = 3;
		}
		if (item instanceof Hive) {
			if (game.stage == 1) enemiesToCreate = 4;
			if (game.stage == 2) enemiesToCreate = 5;
			if (game.stage == 3) enemiesToCreate = 5;
		}
		if (!spawnEnemies) enemiesToCreate = 0;
		const x: number[] = [], y: number[] = [];
		while (enemiesToCreate > 0) {
			const create = weightedSample([
				[10, Enemies.createSpider],
				[10, Enemies.createSnake],
				[5, Enemies.createBird],
			]);
			let enemy = create(game.camera.container, game.stage, 0, 0);

			game.enemies.push(enemy)

			if (item == null) {
				let pos = 0;
				do {
					pos = Math.random() * chunkRadius * 2 | 0
				} while (x.includes(pos))
				enemy.x = (chunkId.x * chunkRadius * 2 - chunkRadius + pos) * tileSize - tileSize / 2
				x.push(pos)

				do {
					pos = Math.random() * chunkRadius * 2 | 0
				} while (y.includes(pos))
				enemy.y = (chunkId.y * chunkRadius * 2 - chunkRadius + pos) * tileSize - tileSize / 2
				y.push(pos)
			} else {
				enemy.aggroStartRange /= 1.7;
				x.push(Math.round(item.x / tileSize))
				let range = 1;
				let pos = 0;
				do {
					pos = Math.round(item.x / tileSize + randomInt(-range, range))
					range += 0.2;
				} while (x.includes(pos))
				enemy.x = pos * tileSize;
				x.push(pos)

				y.push(Math.round(item.y / tileSize))
				range = 1;
				do {
					pos = Math.round(item.y / tileSize + randomInt(-range, range))
					range += 0.2;
				} while (y.includes(pos))
				enemy.y = pos * tileSize
				y.push(pos)
			}

			enemiesToCreate--;
		}
	}

	public getRequiredChunks(centerChunk: PointData): PointData[] {
		const requiredChunks: PointData[] = []
		for (let i = -loadedChunkDepth; i <= loadedChunkDepth; i++) {
			for (let j = -loadedChunkDepth; j <= loadedChunkDepth; j++) {
				requiredChunks.push({
					x: centerChunk.x + i,
					y: centerChunk.y + j
				})
			}
		}
		return requiredChunks
	}

	public tick(game: Game, deltaMS: number) {
		if (game.stage == 0) {
			for (const keyIcon of this.keyIcons) keyIcon.tick(deltaMS);
		} else if (game.stage > 0) {
			const activeChunk = getChunk(game.player)
			if (Geom.equal(activeChunk, this.currentChunk)) {
				return;
			}

			const requiredChunks = this.getRequiredChunks(activeChunk);

			const chunksToUnload = this.loadedChunks.filter(c => !requiredChunks.find(l => Geom.equal(l, c)))
			const chunksToLoad = requiredChunks.filter(c => !this.loadedChunks.find(l => Geom.equal(l, c)))

			for (const chunk of chunksToUnload) {
				this.unloadChunk(game, chunk)
			}

			for (const chunk of chunksToLoad) {
				this.loadChunk(game, chunk, true)
			}

			this.currentChunk = activeChunk;
		}
	}

	public destroy() {
		this.container.removeFromParent();
		this.container.destroy({ children: true });
	}

	private containsItem(game: Game, chunkId: PointData) {
		let minX = (chunkId.x * 2 * chunkRadius - chunkRadius) * tileSize;
		let maxX = (chunkId.x * 2 * chunkRadius + chunkRadius) * tileSize;
		let minY = (chunkId.y * 2 * chunkRadius - chunkRadius) * tileSize;
		let maxY = (chunkId.y * 2 * chunkRadius + chunkRadius) * tileSize;
		for (let item of game.hives) {
			if (item.x > minX && item.x < maxX && item.y > minY && item.y < maxY) return item;
		}
		for (let item of game.items) {
			if (item.x > minX && item.x < maxX && item.y > minY && item.y < maxY) return item;
		}
		return null;
	}
}
